/* eslint-disable react/prop-types,react/no-unstable-nested-components */
import _ from 'lodash';
import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Publish from '@mui/icons-material/Publish';
import Box from '@mui/material/Box';
import BoltIcon from '@mui/icons-material/Bolt';
import Tooltip from '@mui/material/Tooltip';
import { byIdMap } from 'relevant-shared/objects/objectStore';
import GlobalEventDiv from '../../lib/globalEventDiv';
import { ActionButton } from '../ActionButton/ActionButton';
import Disabler from '../Disabler';
import Select from '../Select';
import Checkbox from '../Checkbox';
import { Dialog } from '../Dialog';
import TextField from '../TextField';
import MiscUtils from '../../lib/miscUtils';
import { PopupForm, SimpleAlert } from '../Wrappers';
import classes from '../../api/classes';
import Base from '../../layouts/Base';
import styles from './styles.css';

const { TagPreview } = classes;

const loadExistingPreviewHandler = async (
	{ model: { str }, preventDefault },
	{ model: preview, setPreview, pubSelect, zCss },
) => {
	const showError = (message) => {
		preventDefault();
		Base.renderGlobal((done) => (
			<Dialog
				open
				status="error"
				text={message}
				onClose={done}
				style={{ zIndex: zCss(3) }}
			/>
		));
	};
	const all = await TagPreview.list();
	const existing = all.find((pr) => str.includes(pr.id));
	if (!existing) {
		return showError('This preview has either expired or is not valid');
	}
	if ((preview.publisherId || null) !== (existing.publisherId || null)) {
		const byPubId = byIdMap('Publisher');
		if (existing.publisherId) {
			const otherPub = byPubId[existing.publisherId];
			if (otherPub) {
				if (!pubSelect) {
					return showError((
						<span>
							This preview belongs to the publisher account <b>{otherPub.name}</b>
						</span>
					));
				} // else => that's ok, we're a global preview and changes publisher
			} else {
				return showError('This preview belongs to an unknown publisher account');
			}
		} else {
			return showError('This preview doesn\'t belongs to this publisher');
		}
	}
	setPreview(existing);
	return undefined;
};

function PubSelect({ field, byPubCache, setPreview, model: preview }) {
	const pubMap = byIdMap('Publisher');
	const pubs = MiscUtils.alphaSorted(_.values(pubMap), 'name');
	const onChange = ({ target: { value: publisherId } }) => {
		// We'll never change publisherId on a preview, instead we're creating new ones
		let newPreview = byPubCache[publisherId]; // ...or use a cached one for the publisher
		if (!newPreview) {
			// Keep all settings when switching preview objects
			newPreview = new TagPreview(preview, true);
			newPreview.publisherId = publisherId;
		}
		setPreview(newPreview);
	};
	const pubLabel = (pub) => (!byPubCache[pub.id] ? pub.name : (
		<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
			<b>{pub.name}</b>
			<Tooltip title="Preview link generated">
				<BoltIcon fontSize="medium" style={{ color: 'orange' }} />
			</Tooltip>
		</Box>
	));
	return (
		<Select
			fullWidth
			label="Select publisher account"
			help="Live previews for global settings can only be created for a single publisher"
			{...({ ...field('publisherId'), onChange })}
			items={pubs.map((pub) => ({ value: pub.id, label: pubLabel(pub) }))}
			selectProps={{
				className: preview.publisherId ? '' : styles.pulseEffect,
			}}
		/>
	);
}

function PreviewSettings(params) {
	const { field, model: preview, zCss, pubSelect } = params;
	const { includeDemoBidder: useDemo } = preview;
	const [width, setWidth] = useState(0);
	return (
		<GlobalEventDiv
			callback={(div) => setWidth(div.getBoundingClientRect().width)}
		>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<PopupForm
						title="Load existing preview via preview URL / ID"
						customLink={(open) => (
							<ActionButton
								onClick={open}
								variant="outlined"
								icon={<Publish />}
								label="Load existing preview"
							/>
						)}
						dialogProps={{ sx: { zIndex: zCss(2) } }}
						size="sm"
						getModel={() => ({ str: '' })}
						okLabel="Load"
						actionButtonProps={{
							icon: <Publish />,
							variant: 'outlined',
						}}
						content={({ field: fld }) => (
							<Grid container spacing={3} sx={{ pb: 1 }}>
								<Grid item xs={12}>
									<SimpleAlert
										title="Examples"
										content={(
											<ul style={{ listStyle: 'unset', listStylePosition: 'inside' }}>
												<li>https://www.example.com#rlvLPreview=690b4458a84d29c810271277</li>
												<li>690b4458a84d29c810271277</li>
											</ul>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										{...fld('str')}
										label="Preview URL or preview ID"
										fullWidth
										required
									/>
								</Grid>
							</Grid>
						)}
						onApplyChanges={(p) => loadExistingPreviewHandler(p, params)}
					/>
				</Grid>
				{pubSelect && (
					<Grid item xs={12}>
						<PubSelect {...params} />
					</Grid>
				)}
				<Grid item xs={12}>
					<Select
						{...field('type')}
						label="Create preview using which settings"
						fullWidth
						items={[
							{ label: 'Current settings on screen', value: 'screen' },
							{ label: 'Already saved settings', value: 'db' },
						]}
					/>
				</Grid>
				<Grid item xs={12} style={{ paddingTop: 8 }}>
					<Checkbox
						label="Use demo bidder"
						{...field('includeDemoBidder')}
						help="When using the demo bidder, example prebid ads will be returned - and traffic will not be registered in Headerbid Analytics"
					/>
				</Grid>
				<Grid item xs={12} style={{ paddingTop: 8 }}>
					{/* eslint-disable-next-line react/prop-types */}
					<Disabler disabled={!useDemo}>
						<Card>
							<CardHeader
								title="Demo bidder base CPM ($)"
								sx={{ pt: 1, pr: 0, pb: 0, pl: 1 }}
							/>
							<CardContent>
								<Grid container spacing={2}>
									{['banner', 'video', 'native'].map((type) => (
										<Grid key={type} item xs={width < 300 ? 12 : 4}>
											<TextField
												{...field(`${type}DemoCpm`)}
												label={type.charAt(0).toUpperCase() + type.slice(1)}
												fullWidth
												required
												float
											/>
										</Grid>
									))}
									<Grid item xs={12}>
										<TextField
											{...field('randomCpmIncPerc')}
											label="Randomly increase bids with this %"
											help="Set to 0 in order to get the exact same CPM per media type."
											fullWidth
											required
											float
										/>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Disabler>
				</Grid>
				<Grid item xs={12}>
					<TextField
						{...field('exampleUrl')}
						label="Example destination URL (optional)"
						fullWidth
						url
					/>
				</Grid>
			</Grid>
		</GlobalEventDiv>
	);
}

export default PreviewSettings;
